import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { can } from '../../utils/permissions';
import { errorText } from '../../utils/errorText';
import { withHandoffRequest } from '../../utils/fieldBatches';
import { handoffBody, isOpen, myHandoffs, statusTone } from '../../utils/handoffs';
import Button from '../ui/Button';
import HandoffRequestDialog from './HandoffRequestDialog';
import RareFindPrompt from './RareFindPrompt';
import ReasonDialog from './ReasonDialog';
import useOpenChat from '../chat/openChat';

const KEEP = Object.freeze({ keepSession: true });

/**
 * A record's handoff to the lab, on its collector's own page in Field Mode
 * (FieldSentEditor; B6): what was asked and where it stands -- an
 * invitation the lab sent after a rare species was confirmed here, a request
 * waiting, taken on, scheduled, collected, received, cancelled or declined --
 * and what the collector can do about it: answer the invitation, ask for a
 * pickup or a drop-off, cancel with a reason, message the lab member who
 * took it (the chat's own DM, /chat?c=).
 *
 * A record not yet sent keeps the request on its batch (the outbox sends it
 * once the record is); a sent one asks POST /handoffs at once. `find` is the
 * rare find in the AI answer asked on this page (utils/handoffs.js
 * rareFindOf), which prompts as the camera's summary does.
 *
 * Only the record's own collector sees it: the server takes a request only
 * from them (404 otherwise), and "my handoffs" says nothing of anyone
 * else's record -- a lab member opening a shared link would be told "none"
 * and offered a request bound to fail.
 */
export default function HandoffPanel({ sampleId, record, batch, store, userId, user, find = null }) {
  const { t, i18n } = useTranslation(['handoffs', 'common']);
  // Field Mode has no chat drawer: Message goes to /chat there (chat/openChat.js).
  const openChat = useOpenChat();
  const [handoff, setHandoff] = useState(null);
  const [state, setState] = useState('loading');
  const [asking, setAsking] = useState(false);
  const [reasonFor, setReasonFor] = useState(null);
  const [message, setMessage] = useState('');
  const me = user?.user_id ?? userId;
  const collector = !!record?.collector_id && me != null && String(record.collector_id) === String(me);
  const allowed = can(user, 'handoff:request') && collector;
  const sent = !batch || batch.status === 'sent';

  const load = useCallback(() => {
    if (!allowed || !sampleId) return Promise.resolve();
    return myHandoffs({ config: KEEP })
      .then((mine) => { setHandoff(mine[sampleId] || null); setState('ok'); })
      .catch(() => setState('failed'));
  }, [allowed, sampleId]);

  // Read again once the outbox has sent a request kept on the batch, or the
  // batch went: the server's handoff then stands where "asked for" stood.
  const handoffSent = !!batch?.handoffSent;
  const batchStatus = batch?.status;
  useEffect(() => { load(); }, [load, handoffSent, batchStatus]);

  if (!allowed) return null;
  const queued = !!batch?.handoff && !batch.handoffSent && !sent;
  const open = isOpen(handoff);
  const mayAsk = !!record && record.is_draft === false
    && !(handoff && (open || handoff.status === 'received'));
  // Nobody messages themselves (a lab member who took on their own find).
  const mayMessage = !!handoff?.assignee && open && can(user, 'chat:use')
    && String(handoff.assignee.user_id) !== String(me);
  const when = iso => (iso ? new Date(iso).toLocaleString(i18n.language) : '');

  /** A request: to the server for a sent record, onto the batch otherwise. */
  const ask = async (request, aiId = null) => {
    setMessage('');
    if (!sent && store && batch) {
      const next = await store.update(userId, batch.id, current => withHandoffRequest(current, request, { aiId }));
      if (next) {
        setAsking(false);
        setMessage(t('prompt.saved'));
        return;
      }
    }
    try {
      await api.post('/handoffs', handoffBody(request, sampleId, { aiId }), KEEP);
    } catch (e) {
      throw new Error(errorText(e));
    }
    setAsking(false);
    await load();
  };

  /** A request still on the batch, not asked for after all: it never goes.
   *  Sent meanwhile, it went with the record: the panel then shows the
   *  server's handoff, with Cancel. */
  const dropQueued = async () => {
    setMessage('');
    try {
      const next = await store.update(userId, batch.id, current => withHandoffRequest(current, null));
      if (next) setMessage(t('prompt.removed'));
    } catch (e) {
      setMessage(errorText(e));
    }
  };

  const cancel = async (reason) => {
    try {
      await api.post(`/handoffs/${handoff.handoff_id}/cancel`, { reason }, KEEP);
    } catch (e) {
      throw new Error(errorText(e));
    }
    setReasonFor(null);
    await load();
  };

  const messageAssignee = async () => {
    setMessage('');
    try {
      const res = await api.post('/chat/dms', { user_id: handoff.assignee.user_id });
      openChat(res.data.channel_id);
    } catch (e) {
      setMessage(errorText(e));
    }
  };

  const tone = handoff ? statusTone(handoff.status) : null;
  const why = (handoff?.invite_reasons || []).map(r => t(`reasons.${r}`)).join(', ');

  return (
    <section style={st.section} aria-labelledby="handoff-panel-title" data-testid="handoff-panel"
      data-status={handoff?.status || (queued ? 'queued' : 'none')}>
      <h2 id="handoff-panel-title" style={st.h2}>{t('panel.title')}</h2>
      {queued && (
        <>
          <p style={st.p} data-testid="handoff-panel-queued">
            {t('panel.queued', { method: t(`method.${batch.handoff.method}`) })}
          </p>
          {/* Still on the phone: it can be taken back before it goes. */}
          {store && (
            <div style={st.actions}>
              <Button variant="ghost" onClick={dropQueued} title={t('tip.remove')} data-testid="handoff-panel-remove">
                {t('prompt.remove')}
              </Button>
            </div>
          )}
        </>
      )}
      {batch?.handoffRefused && (
        <p style={st.warn} data-testid="handoff-panel-refused">
          {t('panel.refused', { code: batch.handoffRefused.code || batch.handoffRefused.status })}
        </p>
      )}
      {state === 'failed' && !queued && <p style={st.warn} data-testid="handoff-panel-failed">{t('panel.failed')}</p>}

      {handoff && (
        <div data-testid="handoff-panel-current">
          <p style={st.p}>
            <span style={{ ...st.chip, background: tone.bg, color: tone.fg }} data-testid="handoff-panel-status">
              {t(`status.${handoff.status}`)}
            </span>
            {handoff.method && <span style={st.muted}>{' '}{t(`method.${handoff.method}`)}</span>}
          </p>
          {handoff.status === 'invited' && (
            <p style={st.p} data-testid="handoff-panel-invited">
              {t('panel.invited', { taxon: handoff.taxon_name || '', why })}
            </p>
          )}
          {handoff.assignee && open && (
            <p style={st.p} data-testid="handoff-panel-assignee">{t('panel.assignee', { name: handoff.assignee.name })}</p>
          )}
          {handoff.window_from && open && (
            <p style={st.p} data-testid="handoff-panel-window">
              {t('panel.window', { when: [when(handoff.window_from), when(handoff.window_to)].filter(Boolean).join(' – ') })}
            </p>
          )}
          {handoff.meeting_note && open && <p style={st.p}>{t('panel.meeting', { note: handoff.meeting_note })}</p>}
          {handoff.status === 'received' && (
            <p style={st.p} data-testid="handoff-panel-received">{t('panel.received', { label: handoff.received_label || '' })}</p>
          )}
          {handoff.closed_reason && ['cancelled', 'declined'].includes(handoff.status) && (
            <p style={st.p} data-testid="handoff-panel-reason">{t('panel.reasonShown', { reason: handoff.closed_reason })}</p>
          )}
          <div style={st.actions}>
            {(handoff.actions || []).includes('accept') && (
              <Button variant="primary" onClick={() => setAsking(true)} title={t('tip.accept')}
                data-testid="handoff-panel-accept">
                {t('panel.accept')}
              </Button>
            )}
            {(handoff.actions || []).includes('cancel') && (
              <Button onClick={() => setReasonFor(handoff.status === 'invited' ? 'decline' : 'cancel')}
                title={t('tip.cancelHandoff')} data-testid="handoff-panel-cancel">
                {handoff.status === 'invited' ? t('panel.notPossible') : t('panel.cancel')}
              </Button>
            )}
            {mayMessage && (
              <Button variant="ghost" onClick={messageAssignee} title={t('tip.message')}
                data-testid="handoff-panel-message">
                {t('panel.message', { name: handoff.assignee.name })}
              </Button>
            )}
          </div>
        </div>
      )}

      {find && mayAsk && !queued ? (
        <RareFindPrompt find={find} sentNote="sent" location={null} onRequest={ask} testId="handoff-panel-rare" />
      ) : (
        !handoff && !queued && state === 'ok' && <p style={st.muted} data-testid="handoff-panel-none">{t('panel.none')}</p>
      )}
      {mayAsk && !queued && !find && (
        <Button onClick={() => setAsking(true)} title={t('tip.request')} data-testid="handoff-panel-request">
          {t('panel.request')}
        </Button>
      )}
      {message && <p role="status" style={st.p} data-testid="handoff-panel-message-text">{message}</p>}

      {asking && (
        <HandoffRequestDialog initial={{ method: 'pickup' }} testId="handoff-panel-form"
          onClose={() => setAsking(false)} onSubmit={request => ask(request)} />
      )}
      {reasonFor && (
        <ReasonDialog title={reasonFor === 'decline' ? t('panel.declineTitle') : t('panel.cancelTitle')}
          testId="handoff-panel-reason" onClose={() => setReasonFor(null)} onSubmit={cancel} />
      )}
    </section>
  );
}

const st = {
  section: { marginTop: 14, padding: '12px 14px', border: '1px solid var(--c-border)', borderRadius: 8 },
  h2: { fontSize: 16, margin: '0 0 8px' },
  p: { margin: '0 0 6px', fontSize: 14 },
  warn: { margin: '0 0 6px', fontSize: 14, color: 'var(--c-warning-fg)' },
  muted: { fontSize: 13, color: 'var(--c-text-muted-fg)' },
  chip: { display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600 },
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap', margin: '8px 0' },
};
